import type { Moment } from "moment-timezone";
import moment from "moment-timezone";
import type { IResponseWithLoading, IResponseWithLoadingAndTimestamp } from "App/Modules/Models";
import type { Observable } from "rxjs";
import { pipe } from "rxjs";
import { startWith, pairwise, map, combineLatest } from "rxjs/operators";

export const loadingCompletedResponse = <TData>(data?: TData) =>
    ({
        isLoading: false,
        isPartiallyLoading: false,
        hasError: false,
        hasPartialError: false,
        isSuccessful: true,
        isPartiallySuccessful: true,
        data,
    } as IResponseWithLoading<TData>);

const mergeLoadingAndData = <T>(prev: IResponseWithLoading<T>, current: IResponseWithLoading<T>) => {
    if (
        prev &&
        current &&
        !current.hasError &&
        !current.data &&
        prev.data &&
        (prev.isSuccessful || prev.isPartiallySuccessful)
    ) {
        current.data = prev.data;
        current.hasPartialError = current.hasError || current.hasPartialError;
        current.isPartiallySuccessful = !current.hasError && (prev.isPartiallySuccessful || prev.isSuccessful);
        current.isLoading = false;
        current.isPartiallyLoading = true;
    }

    return current;
};

/** Merges values in observable sequence with previous so data is preserved and isPartiallyLoading is set if underlying sequence is loaded
 *
 * Useful when refreshing already loaded data. First load will not differ.
 */
export const mergeLoadingAndDataWithPrevious = <T>() => {
    return pipe(
        startWith(null), // Fill pairwise buffer
        pairwise<IResponseWithLoading<T>>(),
        map(([prev, current]) => mergeLoadingAndData(prev, current)),
    );
};

export const mergeResponsesWithLoading = (...loadingProps: IResponseWithLoading<void>[]) => {
    let returnProps: IResponseWithLoadingAndTimestamp<void> = null;

    loadingProps.forEach((p) => {
        if (!returnProps) {
            returnProps = { ...p };
            return;
        }
        returnProps = { ...p, ...returnProps };
        returnProps.error = returnProps.error ?? p.error;
        returnProps.hasError = returnProps.hasError && p.hasError;
        returnProps.hasPartialError =
            returnProps.hasError || returnProps.hasPartialError || p.hasError || p.hasPartialError;
        returnProps.isLoading = returnProps.isLoading && p.isLoading;
        returnProps.isPartiallyLoading =
            returnProps.isLoading || p.isLoading || returnProps.isPartiallyLoading || p.isPartiallyLoading;
        returnProps.isSuccessful = returnProps.isSuccessful && p.isSuccessful;
        returnProps.isPartiallySuccessful =
            returnProps.isSuccessful || returnProps.isPartiallySuccessful || p.isSuccessful || p.isPartiallySuccessful;
    });
    if (returnProps.isSuccessful && !returnProps.isLoading && !returnProps.isPartiallyLoading) {
        returnProps.dtLastUpdated = moment();
    }
    returnProps.responses = loadingProps;

    return returnProps;
};

export const applyLocalUpdatesOnFullDataArray = <T extends { id: string }>(
    fullDbResponse$: Observable<IResponseWithLoading<T[]>>,
    patchResponse$: Observable<IResponseWithLoading<T[]>>,
) =>
    patchResponse$.pipe(
        startWith(null), // Fill latestFrom buffer
        // Bridge loading states individually to get partial loading states
        mergeLoadingAndDataWithPrevious(),
        combineLatest(fullDbResponse$.pipe(mergeLoadingAndDataWithPrevious())),
        map(([patchResponse, fullDbResponse]) => {
            if (!patchResponse || !fullDbResponse) {
                return fullDbResponse;
            }

            const { data: patchData, ...patchLoadingState } = patchResponse;
            const { data: fullDbData, ...fullDbLoadingState } = fullDbResponse;

            const loadingState = mergeResponsesWithLoading(fullDbLoadingState, patchLoadingState);

            if (fullDbData?.length && patchData?.length) {
                patchData.forEach((patchedIssue) => {
                    if (!patchedIssue?.id) {
                        return;
                    }
                    // Splice patch updates into latest full db result
                    // This will result in consecutive patches will be spliced into the same original array
                    const index = fullDbData.findIndex((i) => i.id === patchedIssue.id);
                    fullDbData.splice(index, 1, patchedIssue);
                });
            }
            return { data: fullDbData, ...loadingState };
        }),
    );

// eslint-disable-next-line no-nested-ternary, eqeqeq
const defaultComparer = (a, b) => (a == b ? 0 : a > b ? -1 : 1);

export const sort =
    <T extends Record<string, any>, TProperty = number | boolean | string>(
        propertyAccessor: (item: T) => TProperty,
        comparer?: (a: TProperty, b: TProperty) => number,
    ) =>
    (itemA: T, itemB: T, dir: "asc" | "desc") => {
        const a = propertyAccessor(itemA);
        const b = propertyAccessor(itemB);

        if (!comparer) {
            // eslint-disable-next-line
            comparer = defaultComparer;
        }

        // eslint-disable-next-line
        if (a == b) {
            return 0;
        }
        // eslint-disable-next-line
        if (a == undefined) {
            return dir === "desc" ? -1 : 1;
        }
        // eslint-disable-next-line
        if (b == undefined) {
            return dir === "desc" ? 1 : -1;
        }
        return dir === "desc" ? comparer(a, b) : comparer(b, a);
    };

// react-table expects sorter to always return ascending and will then reverse result for descending sorting
// This makes "null last" sorting algorithm a bit odd
export const sortReactTableColumnWithNullLast =
    <T extends Record<string, any>, TProperty = number | boolean | string>(
        propertyAccessor: (item: T) => TProperty,
        comparer?: (a: TProperty, b: TProperty) => number,
    ) =>
    (itemA: T, itemB: T, dir: "asc" | "desc") => {
        const a = propertyAccessor(itemA);
        const b = propertyAccessor(itemB);

        if (!comparer) {
            // eslint-disable-next-line
            comparer = defaultComparer;
        }

        if (a === b) {
            return 0;
        }
        if (a === undefined || a === null) {
            return dir === "asc" ? 1 : -1;
        }
        if (b === undefined || b === null) {
            return dir === "asc" ? -1 : 1;
        }
        return comparer(b, a);
    };

export const sortReactTableMomentColumnWithNullLast = <T extends Record<string, any>>(
    propertyAccessor: (item: T) => Moment,
) =>
    sortReactTableColumnWithNullLast(propertyAccessor, (a: Moment, b: Moment) => {
        const diff = moment(b).diff(moment(a));
        if (diff === 0) {
            return 0;
        }
        if (diff > 0) {
            return 1;
        }
        return -1;
    });
